import * as React from "react";
import { Box, Typography, Stack, CircularProgress } from "@mui/material";
import { keyframes } from "@mui/material/styles";
import CheckCircleRoundedIcon from "@mui/icons-material/CheckCircleRounded";
import RadioButtonUncheckedRoundedIcon from "@mui/icons-material/RadioButtonUncheckedRounded";

/* ------------------------------------------------------------------ *
 * Building blocks of the mission control (MCS) screen: panels with a
 * title bar, status lamps, labelled values and check rows.
 * ------------------------------------------------------------------ */

export const MONO = "'Roboto Mono', 'Courier New', monospace";

export const MCS_COLORS = {
  ok: "#6EE7B7",
  warn: "#FDE68A",
  alarm: "#FCA5A5",
  info: "#67E8F9",
  idle: "rgba(226,232,240,0.45)",
  label: "rgba(196,181,253,0.65)",
};

const pulse = keyframes`
  0%, 100% { opacity: 1; }
  50% { opacity: 0.3; }
`;

export const pad = (n: number, width = 2) => String(Math.floor(n)).padStart(width, "0");

function dayOfYear(d: Date) {
  return Math.floor((d.getTime() - Date.UTC(d.getUTCFullYear(), 0, 0)) / 86400000);
}

// Mission time as mission control writes it: year.day-of-year hh:mm:ss (UTC).
export function formatMissionTime(d: Date) {
  return `${d.getUTCFullYear()}.${pad(dayOfYear(d), 3)} ${pad(d.getUTCHours())}:${pad(d.getUTCMinutes())}:${pad(d.getUTCSeconds())}`;
}

export function formatClock(d: Date) {
  return `${pad(d.getUTCHours())}:${pad(d.getUTCMinutes())}:${pad(d.getUTCSeconds())}`;
}

// h:mm:ss (or m:ss) of a duration in seconds.
export function formatDuration(totalSeconds: number) {
  const s = Math.max(0, Math.round(totalSeconds));
  const h = Math.floor(s / 3600);
  return h > 0 ? `${h}:${pad((s % 3600) / 60)}:${pad(s % 60)}` : `${Math.floor(s / 60)}:${pad(s % 60)}`;
}

export function Panel(props: { title: string; right?: React.ReactNode; children: React.ReactNode; dense?: boolean }) {
  return (
    <Box
      sx={{
        borderRadius: 2,
        border: "1px solid rgba(168,85,247,0.24)",
        background: "linear-gradient(160deg, rgba(20,12,30,0.92) 0%, rgba(12,8,18,0.92) 100%)",
        overflow: "hidden",
        minWidth: 0,
      }}
    >
      <Stack
        direction="row"
        alignItems="center"
        justifyContent="space-between"
        spacing={1}
        sx={{ px: 1.75, py: 0.9, borderBottom: "1px solid rgba(168,85,247,0.18)", bgcolor: "rgba(9,5,11,0.6)" }}
      >
        <Stack direction="row" spacing={1} alignItems="center" sx={{ minWidth: 0 }}>
          <Box sx={{ width: 3, height: 13, borderRadius: 1, bgcolor: "#A78BFA", flexShrink: 0 }} />
          <Typography
            noWrap
            sx={{ fontFamily: MONO, fontSize: 11, fontWeight: 800, letterSpacing: "0.16em", textTransform: "uppercase", color: "#C4B5FD" }}
          >
            {props.title}
          </Typography>
        </Stack>
        {props.right}
      </Stack>
      <Box sx={{ p: props.dense ? 1.5 : { xs: 1.75, sm: 2.25 } }}>{props.children}</Box>
    </Box>
  );
}

export function Led(props: { color: string; pulsing?: boolean; size?: number }) {
  const size = props.size ?? 8;
  return (
    <Box
      sx={{
        width: size,
        height: size,
        borderRadius: "50%",
        bgcolor: props.color,
        boxShadow: `0 0 8px ${props.color}`,
        flexShrink: 0,
        animation: props.pulsing ? `${pulse} 1s ease-in-out infinite` : "none",
      }}
    />
  );
}

// Lamp plus label, e.g. in a panel title bar.
export function StatusLamp(props: { color: string; label: string; pulsing?: boolean }) {
  return (
    <Stack direction="row" spacing={0.75} alignItems="center">
      <Led color={props.color} pulsing={props.pulsing} />
      <Typography sx={{ fontFamily: MONO, fontSize: 11, fontWeight: 800, letterSpacing: "0.1em", color: props.color }}>{props.label}</Typography>
    </Stack>
  );
}

export function DataLabel(props: { children: React.ReactNode }) {
  return (
    <Typography
      sx={{ fontFamily: MONO, fontSize: 10, fontWeight: 700, letterSpacing: "0.14em", textTransform: "uppercase", color: MCS_COLORS.label }}
    >
      {props.children}
    </Typography>
  );
}

// A labelled telemetry-style value.
export function DataField(props: { label: string; value: React.ReactNode; color?: string; stale?: boolean; title?: string }) {
  return (
    <Stack spacing={0.35} sx={{ minWidth: 0 }} title={props.title}>
      <DataLabel>{props.label}</DataLabel>
      <Typography
        noWrap
        component="div"
        sx={{ fontFamily: MONO, fontSize: 13, fontWeight: 700, color: props.stale ? MCS_COLORS.idle : props.color ?? "#FFFFFF" }}
      >
        {props.value}
      </Typography>
    </Stack>
  );
}

export function CheckRow(props: { label: string; state: "pass" | "running" | "pending" }) {
  const { label, state } = props;
  return (
    <Stack direction="row" spacing={1.25} alignItems="center">
      <Box sx={{ width: 18, height: 18, display: "grid", placeItems: "center", flexShrink: 0 }}>
        {state === "pass" ? (
          <CheckCircleRoundedIcon sx={{ fontSize: 18, color: MCS_COLORS.ok }} />
        ) : state === "running" ? (
          <CircularProgress size={15} thickness={5} sx={{ color: MCS_COLORS.info }} />
        ) : (
          <RadioButtonUncheckedRoundedIcon sx={{ fontSize: 18, color: "rgba(226,232,240,0.3)" }} />
        )}
      </Box>
      <Typography sx={{ fontFamily: MONO, fontSize: 12.5, color: state === "pass" ? "rgba(226,232,240,0.92)" : "rgba(226,232,240,0.5)" }}>
        {label}
      </Typography>
    </Stack>
  );
}
